import React, { useState, useEffect } from 'react';
import { makeStyles } from 'react-scale/components/design-system';
import { Typography, Icon, Spinner } from '@scale/scaleui';
import ChatBulkAuditDetails from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditDetails';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
  ChatBulkAuditBatch,
  SubtaskAssignment,
  SubtaskAssignmentAbbreviatedResponse,
} from '../SpeedAudit/types';
import { ITaskAttemptToReturnJson } from '@scale/shared/types/taskattempt';
import {
  useUserTaskInfo,
  useFetchUserInfo,
} from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import _ from 'lodash';

interface ChatBulkAuditTaskerInfoBlockProps {
  auditBatch: ChatBulkAuditBatch;
  attemptAudit: SubtaskAssignment;
  subtaskAssignmentsLocalCopy: SubtaskAssignmentAbbreviatedResponse[];
  contributorId: string;
  attempt: ITaskAttemptToReturnJson;
}

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: '100%',
    gap: '12px',
  },
  scrollable: {
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  spanCenter: {
    alignSelf: 'center',
    marginLeft: '4px',
  },
  spanBottom: {
    alignSelf: 'flex-end',
    marginLeft: '4px',
  },
  rightAlignRow: {
    marginLeft: 'auto !important',
    justifyContent: 'flex-end',
    flexShrink: 0,
    flexGrow: 1,
    display: 'block',
  },
  linkOverride: {
    color: '#6052FF !important',
    textDecoration: 'underline !important',
  },
  divSizeForMarginAutoToWork: {
    width: '80px',
  },
}));

function ChatBulkAuditTaskerInfoBlock(props: ChatBulkAuditTaskerInfoBlockProps) {
  const { attemptAudit, subtaskAssignmentsLocalCopy, contributorId, attempt, auditBatch } = props;

  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [taskIndexForThisContributor, setTaskIndexForThisContributor] = useState<number | null>(
    null,
  );
  const [totalTasksUserCompletedInBatch, setTotalTasksUserCompletedInBatch] = useState<
    number | null
  >(null);
  const classes = useStyles();

  const { data: podInfo, status: podInfoStatus } = useFetchUserInfo(contributorId);

  const { data, isLoading, error } = useUserTaskInfo(contributorId, attempt);
  const { attempter, sbqPercent, sbqRejectedAttempts, sbqTotalAttempts } = data ?? {};

  useEffect(() => {
    if (contributorId) {
      let userTaskCount = 0;
      let taskIndexForContributor = 0;
      let foundSubtask = false;
      for (const subtaskAssignment of subtaskAssignmentsLocalCopy) {
        if (subtaskAssignment.attemptTaskerId === contributorId) {
          userTaskCount++;
          if (attemptAudit._id === subtaskAssignment._id) {
            taskIndexForContributor++;
            foundSubtask = true;
          } else if (!foundSubtask) {
            taskIndexForContributor++;
          }
        }
      }
      setTaskIndexForThisContributor(taskIndexForContributor);
      setTotalTasksUserCompletedInBatch(userTaskCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contributorId, attemptAudit]);

  return (
    <div>
      {attempt && (
        <div className={classes.container}>
          <div className={classes.row}>
            <Typography.Paragraph3>
              Feedback will persist for this contributor across tasks in this batch.
            </Typography.Paragraph3>
          </div>
          <div className={classes.row}>
            <div className={classes.scrollable}>
              <Typography.Subtitle2>
                {!attempter?.firstName && !attempter?.lastName
                  ? '<No name found>'
                  : attempter?.firstName + ' ' + attempter?.lastName}
              </Typography.Subtitle2>
            </div>
            <span className={classes.spanCenter}>
              <Typography.Paragraph2>
                {taskIndexForThisContributor?.toString() ?? '?'}/
                {totalTasksUserCompletedInBatch?.toString() ?? '?'} tasks in this audit
              </Typography.Paragraph2>
            </span>
            <span className={classes.spanCenter + ' ' + classes.rightAlignRow}>
              <div className={classes.rightAlignRow + ' ' + classes.divSizeForMarginAutoToWork}>
                <a className={classes.linkOverride} onClick={() => setOpenDetails(true)}>
                  view details
                </a>
              </div>
            </span>
          </div>
          <div className={classes.row}>
            <Icon icon={['far', 'users']} size="sm" />
            <Typography.Paragraph2>
              Pod name:{' '}
              {podInfoStatus === 'success' ? (
                podInfo?.teamLeadPodType?.name ?? 'No pod found'
              ) : (
                <Spinner size="sm" />
              )}
            </Typography.Paragraph2>
          </div>
          <div className={classes.row}>
            <Icon icon={['far', 'envelope']} size="sm" />
            <div className={classes.scrollable}>
              <Typography.Paragraph2>Email: {attempter?.email}</Typography.Paragraph2>
            </div>
            <CopyToClipboard text={attempter?.email ?? ''}>
              <Icon icon={['far', 'copy']} size="sm" />
            </CopyToClipboard>
          </div>
          <div className={classes.row}>
            <Icon icon={['fas', 'arrow-left']} size="sm" />
            <Typography.Paragraph2>
              SBQ Rate:{' '}
              {!isLoading ? (
                !error && !_.isNil(sbqPercent) ? (
                  `${(sbqPercent * 100).toFixed(2)}%`
                ) : (
                  'Error loading'
                )
              ) : (
                <Spinner size="sm" />
              )}
            </Typography.Paragraph2>
            <Icon
              icon="info-circle"
              size="sm"
              tooltip={`${sbqRejectedAttempts ?? 0} / ${
                sbqTotalAttempts ?? 0
              } tasks SBQ'd in this project`}
            />
          </div>
          <ChatBulkAuditDetails
            open={openDetails}
            onClose={() => setOpenDetails(false)}
            attempt={attempt}
            auditBatch={auditBatch}
            contributorId={attempt.attemptedBy}
            taskInfo={attemptAudit.responses.map(r => ({
              attemptId: r.id,
              reviewLevel: r.attemptedAtReviewLevel,
              reviewerRatingScore: r.reviewerRatingScore,
              reviewStatus: r.reviewStatus,
              reviewOutcome: r.reviewOutcome,
            }))}
            detailType="Contributor"
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(ChatBulkAuditTaskerInfoBlock);
